"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useRouter, usePathname } from "next/navigation";
import { api, setToken, ApiError, type CurrentUser } from "@/lib/api";

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: CurrentUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  // Re-reads who is logged in (after editing your own account).
  refreshUser: () => void;
  // Does the logged-in account have this permission ("media.view", ...)?
  // False until the account has loaded. The API enforces it either way.
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    // Presence of a stored token is treated as "authenticated" for
    // routing purposes; an actually-expired or invalid token still gets
    // rejected by the API on the first real request, which the request()
    // wrapper surfaces as a normal 401 — handled per-page, not here.
    const hasToken = typeof window !== "undefined" && !!localStorage.getItem("access_token");
    setIsAuthenticated(hasToken);
    setIsLoading(false);
    if (!hasToken && pathname !== "/login") {
      router.replace("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Fetched once the token is confirmed present, not inline above —
  // GET /auth/me is a real network call, and this keeps the synchronous
  // "do we have a token at all" routing check above untangled from it. A
  // failure here (expired/invalid token) just leaves the account menu
  // showing nothing — the same request failing anywhere else in the
  // dashboard already triggers the normal 401 handling.
  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null);
      return;
    }
    api.me().then(setUser).catch(() => {});
  }, [isAuthenticated]);

  async function login(email: string, password: string) {
    const { access_token } = await api.login(email, password);
    setToken(access_token);
    setIsAuthenticated(true);
    // The phone manager (/m) asks to come back to itself after signing in.
    let next = "/";
    try {
      const wanted = sessionStorage.getItem("after_login");
      sessionStorage.removeItem("after_login");
      if (wanted === "/m") next = wanted;
    } catch {
      // not available; go to the dashboard
    }
    router.replace(next);
  }

  function logout() {
    setToken(null);
    setIsAuthenticated(false);
    setUser(null);
    router.replace("/login");
  }

  function refreshUser() {
    api.me().then(setUser).catch(() => {});
  }

  function can(permission: string) {
    return !!user?.permissions.includes(permission);
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, refreshUser, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { ApiError };
